import React, { useState } from 'react';
import type { CentralTopics } from '../../services/MendsVisionService';

type Props = {
  topics: CentralTopics | null;
  loading: boolean;
  error: string | null;
  onOpen: (path: string) => void;
  onRetry: () => void;
};

function shortDate(iso: string): string {
  const [y, m, d] = iso.split('-');
  return y && m && d ? `${d}/${m}` : iso;
}

/**
 * Top assuntos do período (Central → Visão geral). Cada conversa (cliente/dia) é classificada uma vez pela IA numa lista
 * fixa de assuntos, com um trecho real do cliente; os exemplos abrem a conversa.
 */
export const CentralTopicsPanel: React.FC<Props> = ({ topics, loading, error, onOpen, onRetry }) => {
  const [showHow, setShowHow] = useState(false);
  const [expanded, setExpanded] = useState<string | null>(null);
  const base = topics?.contactsClassified ?? 0;
  const max = Math.max(1, ...(topics?.topics ?? []).map((t) => t.contacts));

  return (
    <section className="mai-ci-panel mai-ci-topics" aria-label="Sobre o que os clientes falam">
      <div className="mai-ci-section-head">
        <h2>Sobre o que os clientes falam</h2>
        <button
          type="button"
          className="mai-ci-how"
          aria-expanded={showHow}
          aria-label="Como é calculado: assuntos"
          onClick={() => setShowHow((s) => !s)}
        >
          ?
        </button>
      </div>
      {showHow && topics?.howCalculated && <p className="mai-ci-kpi-how">{topics.howCalculated}</p>}

      {error ? (
        <div className="mai-ci-empty">
          <p>{error}</p>
          <button type="button" className="mai-ci-link" onClick={onRetry}>Tentar de novo</button>
        </div>
      ) : !topics ? (
        <p className="mai-ci-empty" aria-busy="true">Lendo as conversas do período…</p>
      ) : !topics.available ? (
        <p className="mai-ci-empty">Indisponível: {topics.unavailableReason}</p>
      ) : topics.total === 0 ? (
        <p className="mai-ci-empty">Nenhum cliente escreveu no período.</p>
      ) : (
        <>
          {topics.topics.length === 0 ? (
            <p className="mai-ci-empty">
              {topics.pending > 0 ? 'Ainda classificando as conversas do período…' : 'Nenhum assunto identificado nas conversas do período.'}
            </p>
          ) : (
            <ul className="mai-ci-topic-list">
              {topics.topics.map((t) => {
                const open = expanded === t.key;
                return (
                  <li key={t.key}>
                    <button
                      type="button"
                      className="mai-ci-topic"
                      aria-expanded={open}
                      title={t.description}
                      onClick={() => setExpanded(open ? null : t.key)}
                    >
                      <span className="mai-ci-bar-label">{t.label}</span>
                      <span className="mai-ci-bar-track"><span style={{ width: `${(t.contacts / max) * 100}%` }} /></span>
                      <span className="mai-ci-bar-value">
                        {t.contacts}
                        <small>{base > 0 ? ` · ${Math.round((t.contacts * 100) / base)}%` : ''}</small>
                      </span>
                    </button>
                    {open && (
                      <div className="mai-ci-topic-examples">
                        <p className="mai-ci-muted">
                          {t.contacts} cliente{t.contacts === 1 ? '' : 's'} em {t.conversations} conversa{t.conversations === 1 ? '' : 's'} · {t.description}
                        </p>
                        <ul>
                          {t.examples.map((e) => (
                            <li key={`${e.sectorId}-${e.contactId}-${e.date}`}>
                              <blockquote>“{e.quote}”</blockquote>
                              <button type="button" className="mai-ci-link" onClick={() => onOpen(e.path)}>
                                {e.contactName} · {shortDate(e.date)} · abrir conversa
                              </button>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
          <p className="mai-ci-muted mai-ci-topics-foot">
            {topics.classified} de {topics.total} conversas classificadas
            {topics.noTopic > 0 ? ` · ${topics.noTopic} sem assunto (só cumprimento, nome ou mídia)` : ''}
            {topics.failed > 0 ? ` · ${topics.failed} sem classificação válida` : ''}
            {base > 0 ? ` · % sobre ${base} clientes com conversa classificada; um cliente pode falar de mais de um assunto` : ''}
            {loading && topics.pending > 0 ? ` · classificando mais ${topics.pending}…` : ''}
          </p>
        </>
      )}
    </section>
  );
};
